import { VStack, Image, Text, Avatar, Stack, Box, Flex, TextProps } from '@chakra-ui/react'
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'

type QuoteBlockProps = {
  quote: string
  quoteTextProps?: TextProps
  avatarUrl?: string
  name: string
  title: string
  logoUrl: string
  logoAlt: string
  logoWidth: string
  containerProps?: any
  /**
   * Optional secondary callout text, e.g., to show a raise amount, accomplishment, or milestone.
   * Example: "$135M Raised", "Inc 500 Founder", etc.
   */
  secondaryCalloutText?: string
  showVisual?: boolean
  visualUrl?: string
  visualAlt?: string
  visualBgGradient?: string
}

export const QuoteBlock: React.FC<QuoteBlockProps> = props => {
  const { getBreakpointValue } = useBreakpointContext()
  const isMobile = getBreakpointValue([true, true, false, false, false])
  return (
    <VStack align={'center'} flex={1} gap={10} justify={'center'} padding={0} {...props.containerProps}>
      <Stack
        align={'center'}
        direction={props.showVisual ? 'row' : 'column'}
        gap={4}
        justify={'center'}
        width={'full'}
      >
        <VStack align={'center'} flex={1} gap={getBreakpointValue([6, 10])} justify={'center'}>
          <Image
            alt={props.logoAlt}
            filter={{ _light: 'invert(1) saturate(0)', _dark: 'invert(0) saturate(1)' }}
            objectFit={'contain'}
            src={props.logoUrl}
            width={props.logoWidth}
          />
          <Text
            color={'red.fg/75'}
            fontFamily={'longform'}
            fontSize={getBreakpointValue(['lg', 'lg', 'xl', '2xl', '2xl'])}
            fontStyle={'italic'}
            fontWeight={'500'}
            maxWidth={'container.md'}
            opacity={1}
            textAlign={'center'}
            {...props.quoteTextProps}
          >
            {props.quote}
          </Text>
          <Stack
            align={'center'}
            direction={getBreakpointValue(['column', 'row'])}
            flex={1}
            gap={4}
            justify={'center'}
          >
            <Avatar.Root size={getBreakpointValue(['md', 'lg', '2xl'])}>
              <Avatar.Fallback name={props.name} />
              <Avatar.Image src={props.avatarUrl} />
            </Avatar.Root>
            <VStack align={getBreakpointValue(['center', 'flex-start'])} gap={0} justify={'center'}>
              <Text
                color={'colorPalette.fg'}
                fontSize={getBreakpointValue(['md', 'lg', 'xl'])}
                fontWeight={600}
                opacity={1}
                textAlign={getBreakpointValue(['center', 'left'])}
              >
                {props.name}
              </Text>
              <Text
                color={'red.fg/75'}
                fontWeight={600}
                opacity={1}
                textAlign={getBreakpointValue(['center', 'left'])}
              >
                {props.title}
              </Text>
              {props.secondaryCalloutText && (
                <Text
                  color={'colorPalette.border'}
                  opacity={1}
                  textAlign={getBreakpointValue(['center', 'left'])}
                  textStyle={getBreakpointValue(['labelSm', 'labelMd'])}
                >
                  {props.secondaryCalloutText}
                </Text>
              )}
            </VStack>
          </Stack>
        </VStack>
        {props.showVisual && (
          <Flex
            align={'center'}
            alignSelf={'stretch'}
            borderRadius={'md'}
            direction={'column'}
            flex={1}
            justify={'center'}
            position={'relative'}
          >
            <Box
              height={'700px'}
              position={'absolute'}
              width={'700px'}
              bgImage={
                props.visualBgGradient
                  ? `radial-gradient({colors.${props.visualBgGradient}}, transparent 70%)`
                  : undefined
              }
            />
            <Image
              alt={props.visualAlt}
              objectFit={'contain'}
              src={props.visualUrl}
              width={'full'}
              zIndex={1}
            />
            <Box height={'full'} left={0} position={'absolute'} top={0} width={'full'}>
              <Image
                alt={props.logoAlt}
                filter={{ _light: 'invert(1) saturate(0)', _dark: 'invert(0) saturate(1)' }}
                height={'full'}
                objectFit={'contain'}
                opacity={0.1}
                src={props.logoUrl}
                width={'full'}
              />
            </Box>
          </Flex>
        )}
      </Stack>
    </VStack>
  )
}
